import { faBookmark, faPeopleGroup, faTimeline } from "@fortawesome/free-solid-svg-icons";
import FruitInput from "components/entities/agriculture/fruit/inputs/FruitInput";
import PersonDropdown from "components/entities/app/person/partials/inputs/PersonDropdown";
import SeasonDropdown, { SEASON_ACTIVE_FILTER_ID } from "components/entities/app/season/inputs/SeasonDropdown";
import { AppContextManager } from "config/AppContext";
import { ClassNameExtender, DatalistDataSourceFilterDefinitionType, DatalistTableColumnType } from "types/datalist-types";
import { FruitFilterInput, FruitSortInput, ListFilterInputTypeOfWorkerFilterInput, PersonFilterInput, PersonSortInput, SeasonFilterInput, SeasonSortInput, SortEnumType, StringOperationFilterInput } from "types/graphql";
import { Conditions, equalFilterConditions, nullFilterConditions, resolveSearchFilterPropertyName, setFilterInput } from "./datalist-filter-utilities";
import ObjectUtilities from "utilities/common-utilities/object-utilities";
import If from "components/common/If";
import HTMLCode from "components/common/HTMLCode";
import { DeepPartial } from "@apollo/client/utilities";
import * as lodash from'lodash';

export const isVisible = <T, TFilter, TSort>(column: DatalistTableColumnType<T, TFilter, TSort>): boolean => {
    if (!column.hide) return true;
    if (column.hide && typeof column.hide == 'function') return !column.hide();
    return !column.hide;
}

export function getBottomClassName(hasBottomNavigation: boolean | undefined) {
    return {
        table: hasBottomNavigation ? 'bottom-app-header md:bottom-0' : '',
        card: !hasBottomNavigation ? 'bottom-app-header-negative md:bottom-0' : ''
    }
}

export function resolveBottomClassName(classNameExtender: ClassNameExtender, defaultClassName: string): string {
    const bottomClassName = classNameExtender.bottom || 'bottom-0';
    let className = `${bottomClassName} ${defaultClassName}`;

    if (classNameExtender.className) {
        if (!classNameExtender.appendReplaceClasses) className = classNameExtender.className;
        else {
            if (typeof classNameExtender.appendReplaceClasses == 'string') {
                classNameExtender.appendReplaceClasses.split(' ').forEach(classNameItem => {
                    classNameItem = classNameItem.trim();
                    if (!classNameItem) return;
                    className = className.replace(classNameItem, '');
                });
            }

            className += ` ${classNameExtender.className}`;
        }
    }

    return className;
}

export class DatalistColumnDefinitions {

    static seasonColumnDefinition<T extends { season: { name: string } }, TFilterType extends { season?: SeasonFilterInput }, TSortType extends { season?: SeasonSortInput }>(context: AppContextManager, group?: string, short?: boolean, card?: DatalistTableColumnType<T, TFilterType, TSortType>['card']): DatalistTableColumnType<T, TFilterType, TSortType> {
        return {
            name: 'season',
            title: 'fields.season.name',
            resolve: (i: T) => i.season.name,
            sort: {
                resolve(direction: SortEnumType) {
                    return {
                        season: {
                            name: direction
                        }
                    } as TSortType
                },
            },
            filter: {
                type: 'reference',
                resolve(i: DatalistDataSourceFilterDefinitionType) {
                    if (typeof i.value == 'object' && 'id' in i.value && i.value.id === SEASON_ACTIVE_FILTER_ID) {
                        return {
                            season: {
                                active: {
                                    eq: true
                                }
                            }
                        } as TFilterType
                    } else {
                        return setFilterInput('season', i.condition!, i.value, 'reference');
                    }
                },
                valueJSX: [{
                    name: 'season-eq',
                    renderInput: (filter: any, onChange: any) => {
                        return (
                            <SeasonDropdown
                                required
                                onChange={onChange}
                                value={filter.value.value}
                                name={`sale-datalist-season-filter-${filter.value.id}`}
                                placeholder={context.t('data-list.filter.value')}
                                label={false}
                                addActive
                                search="off"
                            />
                        );
                    },
                    conditionType: equalFilterConditions
                }]
            },
            card: {
                title: context.t('fields.season.name'),
                icon: faTimeline,
                group: group,
                resolve: (i: T) => short ? i.season.name.replace("Season ", "S. ") : i.season.name,
                ...card
            }
        };
    }

    static seasonColumnDefinitionType<T, TFilterType, TSortType>(context: AppContextManager, path: string[], group?: string, short?: boolean, card?: DatalistTableColumnType<T, TFilterType, TSortType>['card']): DatalistTableColumnType<T, TFilterType, TSortType> {
        return {
            name: 'season',
            title: 'fields.season.name',
            resolve: (i: T) => ObjectUtilities.getObjectValue(i, path),
            sort: {
                resolve(direction: SortEnumType) {
                    return {
                        season: {
                            name: direction
                        }
                    } as TSortType
                },
            },
            filter: {
                type: 'reference',
                resolve(i: DatalistDataSourceFilterDefinitionType) {
                    if (typeof i.value == 'object' && 'id' in i.value && i.value.id === SEASON_ACTIVE_FILTER_ID) {
                        return {
                            season: {
                                active: {
                                    eq: true
                                }
                            }
                        } as TFilterType
                    } else {
                        return setFilterInput('season', i.condition!, i.value, 'reference');
                    }
                },
                valueJSX: [{
                    name: 'season-eq',
                    renderInput: (filter: any, onChange: any) => {
                        return (
                            <SeasonDropdown
                                required
                                onChange={onChange}
                                value={filter.value.value}
                                name={`sale-datalist-season-filter-${filter.value.id}`}
                                placeholder={context.t('data-list.filter.value')}
                                label={false}
                                addActive
                                search="off"
                            />
                        );
                    },
                    conditionType: equalFilterConditions
                }]
            },
            card: {
                title: context.t('fields.season.name'),
                icon: faTimeline,
                group: group,
                resolve: (i: T) => short ? ObjectUtilities.getObjectValue(i, path).name.replace("Season ", "S. ") : ObjectUtilities.getObjectValue(i, path).name,
                ...card
            }
        };
    }

    static nameColumnDefinition<T extends { name: string }, TFilterType extends StringOperationFilterInput, TSortType extends { name?: SortEnumType }>(context: AppContextManager, group?: string, _short?: boolean, card?: DatalistTableColumnType<T, TFilterType, TSortType>['card']): DatalistTableColumnType<T, TFilterType, TSortType> {
        return {
            name: 'name',
            title: 'fields.name.name',
            resolve: (i: T) => i.name,
            sort: {
                resolve: i => {
                    return {
                        name: i
                    } as TSortType;
                }
            },
            filter: {
                type: 'string',
                resolve(value) {
                    let filter = {} as any;
                    switch (value.condition) {
                        case Conditions.startsWith:
                            filter = {
                                or: [
                                    {
                                        name: {
                                            startsWith: value.value
                                        }
                                    },
                                    {
                                        normalizedName: {
                                            startsWith: '|' + value.value?.toLowerCase()
                                        }
                                    }
                                ]
                            };
                            break;
                        case Conditions.endsWith:
                            filter = {
                                or: [
                                    {
                                        name: {
                                            endsWith: value.value
                                        }
                                    },
                                    {
                                        normalizedName: {
                                            endsWith: value.value?.toLowerCase() + '|'
                                        }
                                    }
                                ]
                            };
                            break;
                        case Conditions.nstartsWith:
                            filter = {
                                or: [
                                    {
                                        name: {
                                            nstartsWith: value.value
                                        }
                                    },
                                    {
                                        normalizedName: {
                                            nstartsWith: '|' + value.value?.toLowerCase()
                                        }
                                    }
                                ]
                            };
                            break;
                        case Conditions.nendsWith:
                            filter = {
                                or: [
                                    {
                                        name: {
                                            nendsWith: value.value
                                        }
                                    },
                                    {
                                        normalizedName: {
                                            nendsWith: value.value?.toLowerCase() + '|'
                                        }
                                    }
                                ]
                            };
                            break;
                        case Conditions.eq:
                            filter = {
                                or: [
                                    {
                                        name: {
                                            eq: value.value
                                        }
                                    },
                                    {
                                        normalizedName: {
                                            eq: "|" + value.value?.toLowerCase() + '|'
                                        }
                                    }
                                ]
                            };
                            break;
                        case Conditions.neq:
                            filter = {
                                or: [
                                    {
                                        name: {
                                            neq: value.value
                                        }
                                    },
                                    {
                                        normalizedName: {
                                            neq: "|" + value.value?.toLowerCase() + '|'
                                        }
                                    }
                                ]
                            };
                            break;
                        default:
                            let nameFilter: any = {
                                name: {}
                            };
                            let normlizedNameFilter: any = {
                                normalizedName: {}
                            };

                            nameFilter.name[value.condition || Conditions.contains] = value.value;
                            normlizedNameFilter.normalizedName[value.condition || Conditions.contains] = value.value;

                            filter = {
                                or: [
                                    nameFilter,
                                    normlizedNameFilter
                                ]
                            };
                            break;
                    }

                    return filter as TFilterType;
                },
            },
            card: {
                title: context.t('fields.name.name'),
                icon: faBookmark,
                group: group,
                resolve: (i: T) => i.name,
                ...card
            }
        };
    }

    static personColumnDefinition<T extends { person: { name: string, country?: { alpha3Code: string, flag: string } | null } }, TFilterType extends { person?: PersonFilterInput }, TSortType extends { person?: PersonSortInput }>(_context: AppContextManager): DatalistTableColumnType<T, TFilterType, TSortType> {
        return {
            name: 'person',
            title: 'fields.person.name',
            resolve: i => <div className="flex text-center gap-1 w-max mx-auto">
                <span>{i.person!.name}</span>
                <If condition={i.person.country}>
                    <HTMLCode />
                    <img className="max-w-6 my-auto" src={i.person.country?.flag} alt={i.person.country?.alpha3Code} />
                </If>
            </div>,
            filter: {
                type: 'reference',
                valueJSX: [
                    {
                        conditionType: equalFilterConditions,
                        name: 'person-eq',
                        renderInput: (filter: any, onChange: any) => {
                            return <PersonDropdown
                                label={false}
                                required
                                search="on"
                                name={`sale-datalist-person-filter-${filter.value.id}`}
                                onChange={onChange}
                                value={filter.value.value}
                                placeholder={'data-list.filter.value'}
                            />
                        },
                    }
                ],
                resolve(value) {
                    let condition = {} as any;
                    condition[value.condition!] = typeof value.value == 'object' && 'id' in value.value ? value.value.id : value.value;
                    if (equalFilterConditions.indexOf(value.condition as any) > -1) {
                        return {
                            person: {
                                id: condition
                            }
                        } as TFilterType
                    } else if (nullFilterConditions.indexOf(value.condition as any) > -1) {
                        return {
                            person: {
                                id: null
                            }
                        } as TFilterType
                    } else {
                        const filter = resolveSearchFilterPropertyName(['person'], value.value, value.condition) as any;
                        return filter as TFilterType;
                    }
                },
            },
            sort: {
                resolve: i => {
                    return {
                        person: {
                            name: i
                        }
                    } as TSortType;
                }
            },
        };
    }

    static personColumnDefinitionType<T, TFilterType, TSortType>(_context: AppContextManager, path?: string[], config: DeepPartial<DatalistTableColumnType<T, TFilterType, TSortType>> | undefined = undefined): DatalistTableColumnType<T, TFilterType, TSortType> {
        const defaultConfig: DatalistTableColumnType<T, TFilterType, TSortType> = {
            name: 'person',
            title: 'fields.person.name',
            resolve: i => ObjectUtilities.getObjectValue(i, path || ['person', 'name']) || '-',
            filter: {
                type: 'reference',
                path: path,
                valueJSX: [{
                    conditionType: equalFilterConditions,
                    name: 'person-eq',
                    renderInput: (filter: any, onChange: any) => {
                        return <PersonDropdown
                            label={false}
                            required
                            search="on"
                            name={`sale-datalist-person-filter-${filter.value.id}`}
                            onChange={onChange}
                            value={filter.value.value}
                            placeholder={'data-list.filter.value'}
                        />
                    },
                }]
            },
            sort: {
                resolve: i => {
                    let sort: any = {
                    };

                    const sortValue = {
                        person: {
                            name: i
                        }
                    };

                    if (path?.length) {
                        sort = ObjectUtilities.defineObjectProperties(sort, path, sortValue)
                    } else {
                        sort = sortValue;
                    }


                    return sort as TSortType;
                }
            },
        };

        return lodash.merge({}, defaultConfig, config) as DatalistTableColumnType<T, TFilterType, TSortType>;
    }

    static workerCountColumnDefinition<T extends { workers: { count: number } }, TFilterType extends { workers?: ListFilterInputTypeOfWorkerFilterInput }, TSortType>(_context: AppContextManager): DatalistTableColumnType<T, TFilterType, TSortType> {
        return {
            name: 'workersCount',
            resolve: i => i.workers?.count,
            filter: {
                type: 'reference',
                valueJSX: [
                    {
                        conditionType: equalFilterConditions,
                        name: 'person-eq',
                        renderInput: (filter, onChange) => {
                            return <PersonDropdown
                                label={false}
                                required
                                search="on"
                                name={`sale-datalist-person-filter-${filter.value.id}`}
                                onChange={onChange}
                                value={filter.value.value}
                                placeholder={'data-list.filter.value'}
                            />
                        }
                    }
                ],
                resolve: i => {
                    let condition = {} as any;
                    condition[i.condition!] = typeof i.value == 'object' && 'id' in i.value ? i.value.id : i.value;
                    if (equalFilterConditions.indexOf(i.condition as any) > -1) {
                        return {
                            workers: {
                                some: {
                                    person: {
                                        id: condition
                                    }
                                }
                            }
                        } as TFilterType
                    } else if (nullFilterConditions.indexOf(i.condition as any) > -1) {
                        return {
                            workers: {
                                some: {
                                    person: {
                                        id: null
                                    }
                                }
                            }
                        } as TFilterType
                    } else {
                        const filter = resolveSearchFilterPropertyName(['workers', 'some', 'person'], i.value, i.condition) as any;
                        return filter as TFilterType;
                    }
                }
            },
            card: {
                icon: faPeopleGroup
            }
        }
    }

    static fruitColumnDefinition<T extends { fruit: { name: string } }, TFilterType extends { fruit?: FruitFilterInput }, TSortType extends { fruit?: FruitSortInput }>(_context: AppContextManager): DatalistTableColumnType<T, TFilterType, TSortType> {
        return {
            name: 'fruit',
            title: 'fields.fruit.shortName',
            filter: {
                type: 'reference',
                valueJSX: [{
                    conditionType: equalFilterConditions,
                    name: "fruit-eq",
                    renderInput(filter, onChange) {
                        return <FruitInput
                            required
                            onChange={onChange}
                            value={filter.value.value}
                            name={`sale-datalist-fruit-filter-${filter.value.id}`}
                            placeholder='data-list.filter.value'
                            label={false}
                        />
                    },
                }]
            },
            sort: {
                resolve(direction) {
                    return {
                        fruit: {
                            name: direction
                        }
                    } as TSortType;
                }
            },
            resolve: i => i.fruit?.name,
        }
    }
}
